/**
 * Large Files Browser (virtual hierarchy: Project → Phases → Tasks → Actions)
 * Lists files from network drive by folder context; download via document or file endpoint.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import { Description, Folder, Storage } from '@mui/icons-material';
import type { LargeFileInfo } from '../../services/api';
import { apiService } from '../../services/api';

interface FolderItem {
  id: number;
  name: string;
  type: 'phase' | 'task' | 'action';
}

interface LargeFilesBrowserProps {
  projectId: number;
  onFileSelect?: (file: LargeFileInfo) => void;
}

export default function LargeFilesBrowser({ projectId, onFileSelect }: LargeFilesBrowserProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [files, setFiles] = useState<LargeFileInfo[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<{ id: number; name: string; type: string }[]>([]);
  const [context, setContext] = useState<{
    projectId: number;
    phaseId?: number;
    taskId?: number;
    actionId?: number;
  }>({ projectId });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getLargeFiles({
        projectId: context.projectId,
        phaseId: context.phaseId,
        taskId: context.taskId,
        actionId: context.actionId,
      })
      .then((res) => {
        if (cancelled) return;
        setFolders(
          (res.folders || []).map((f) => ({
            id: f.id,
            name: f.name,
            type: f.type as 'phase' | 'task' | 'action',
          }))
        );
        setFiles(res.files || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load large files');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [context.projectId, context.phaseId, context.taskId, context.actionId]);

  const handleFolderClick = (folder: FolderItem) => {
    if (folder.type === 'phase') {
      setContext({ projectId, phaseId: folder.id });
      setBreadcrumb([{ id: folder.id, name: folder.name, type: 'phase' }]);
    } else if (folder.type === 'task') {
      setContext((prev) => ({ ...prev, taskId: folder.id, actionId: undefined }));
      setBreadcrumb((prev) => [...prev.filter((b) => b.type === 'phase'), { id: folder.id, name: folder.name, type: 'task' }]);
    } else {
      setContext((prev) => ({ ...prev, actionId: folder.id }));
      setBreadcrumb((prev) => [...prev.filter((b) => b.type !== 'action'), { id: folder.id, name: folder.name, type: 'action' }]);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    const target = breadcrumb[index];
    if (!target) {
      setContext({ projectId });
      setBreadcrumb([]);
      return;
    }
    if (target.type === 'phase') {
      setContext({ projectId, phaseId: target.id });
      setBreadcrumb(breadcrumb.slice(0, index + 1));
    } else if (target.type === 'task') {
      setContext((prev) => ({ ...prev, taskId: target.id, actionId: undefined }));
      setBreadcrumb(breadcrumb.slice(0, index + 1));
    } else {
      setContext((prev) => ({ ...prev, actionId: target.id }));
      setBreadcrumb(breadcrumb.slice(0, index + 1));
    }
  };

  const handleFileClick = async (file: LargeFileInfo) => {
    if (onFileSelect) {
      onFileSelect(file);
      return;
    }
    try {
      let blob: Blob;
      if (file.actionDocumentId) {
        blob = await apiService.downloadDocument(file.actionDocumentId);
      } else {
        blob = await apiService.downloadFile(file.fileId, 'LOCAL');
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Storage fontSize="small" /> Large Files
      </Typography>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            setContext({ projectId });
            setBreadcrumb([]);
          }}
          sx={{ cursor: 'pointer', fontWeight: breadcrumb.length === 0 ? 'bold' : 'normal' }}
        >
          Project Root
        </Link>
        {breadcrumb.map((b, i) => (
          <Link
            key={`${b.type}-${b.id}`}
            component="button"
            variant="body2"
            onClick={() => handleBreadcrumbClick(i)}
            sx={{ cursor: 'pointer' }}
          >
            {b.name}
          </Link>
        ))}
      </Breadcrumbs>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={32} />
        </Box>
      )}
      {!loading && !error && (
        <List dense>
          {folders.map((folder) => (
            <ListItem key={`${folder.type}-${folder.id}`} disablePadding>
              <ListItemButton onClick={() => handleFolderClick(folder)}>
                <ListItemIcon>
                  <Folder color="primary" />
                </ListItemIcon>
                <ListItemText primary={folder.name} secondary={folder.type} />
              </ListItemButton>
            </ListItem>
          ))}
          {files.map((file) => (
            <ListItem key={file.fileId} disablePadding>
              <ListItemButton onClick={() => handleFileClick(file)}>
                <ListItemIcon>
                  <Description />
                </ListItemIcon>
                <ListItemText
                  primary={file.fileName}
                  secondary={`${formatSize(file.fileSize)} · ${file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : ''}`}
                />
                <Chip size="small" label="Large File" sx={{ ml: 1 }} />
              </ListItemButton>
            </ListItem>
          ))}
          {folders.length === 0 && files.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No items in this folder
            </Typography>
          )}
        </List>
      )}
    </Paper>
  );
}
